import React, { useEffect, useState } from "react";
import { IoMdEye, IoMdCreate } from "react-icons/io";
import { RiImageAddFill } from "react-icons/ri";
import { MdDelete } from "react-icons/md";
import { Link } from "react-router-dom";
import Pagination from "../../../components/common/Pagination";

import {
    getAllAdvertisements,
    updateAdvertisement,
    deleteAdvertisement,
    updateBanner,
    addAdvertisement,
    deleteBanner,
    getBannerSettings
} from "../../../api/admin/advertisements";
import { getHotelsRestaurantsListing } from "../../../api/common/index";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { useTranslation } from "../../../hooks/useTranslation";
import Select from "react-select";

const PopupModal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
            <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg max-h-[90vh] overflow-y-auto">
                <button
                    onClick={onClose}
                    className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black text-xl z-10"
                >
                    ✕
                </button>
                {children}
            </div>
        </div>
    );
};

const AdvertisementList = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [selected, setSelected] = useState("hotels");
    const [page, setPage] = useState(1);
    const limit = 10;
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isBannerEditOpen, setIsBannerEditOpen] = useState(false);
    const [formData, setFormData] = useState({
        restaurantId: "",
        hotelId: "",
        placement: "",
        priority: 0,
        startDate: "",
        endDate: "",
        _id: "",
        image: null,
        imagePreview: null,
    });
    const [isEditMode, setIsEditMode] = useState(false);

    // const placementOptions = [
    //     { value: "homepage", label: "Homepage" },
    //     { value: "search_top", label: "Search Top" },
    //     { value: "category_top", label: "Category Top" },
    // ];

    const placementOptions = [
        { value: "homepage", label: t("advertisements").placementOptions.homepage },
        { value: "search_top", label: t("advertisements").placementOptions.search_top },
        { value: "category_top", label: t("advertisements").placementOptions.category_top },
    ];

    const [bannerData, setBannerData] = useState(null);
    const [errors, setErrors] = useState({});
    const [bannerErrors, setBannerErrors] = useState({});

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ["advertisements", page, selected],
        queryFn: () =>
            getAllAdvertisements({
                page,
                limit,
                showRestaurants: selected === "restaurants",
            }),
        keepPreviousData: true,
    });

    const advertisements = data?.data?.advertisements || [];
    const pagination = data?.data?.pagination || {};

    const { data: listingData } = useQuery({
        queryKey: ["hotelsRestaurantsListing", selected],
        queryFn: () =>
            getHotelsRestaurantsListing({
                showRestaurants: selected === "restaurants",
            }),
    });

    const { data: bannerListing } = useQuery({
        queryKey: ["banner-settings"],
        queryFn: getBannerSettings,
    });



    const { mutate: addAdvertisementMutation, isPending: isAdding } = useMutation({
        mutationFn: (payload) => addAdvertisement(payload),
        onSuccess: () => {
            queryClient.invalidateQueries(["advertisements"]);
            closeModal();
            toast.success(t("advertisements").addSuccess || "Advertisement added successfully");
        },
        onError: (error) => {
            toast.error(error?.response?.data?.message || t("common").error);
        },
    });

    const { mutate: updateBannerMutation, isPending: isUpdatingBanner } = useMutation({
        mutationFn: (payload) => updateBanner(payload),
        onSuccess: () => {
            queryClient.invalidateQueries("banner-settings")
            toast.success(t("banner").updateSuccess);
            closeBannerModal();
        },
        onError: (error) => toast.error(error?.response?.data?.message || t("common").error),
    });

    const { mutate: deleteBannerMutation, isPending: isDeletingBanner } = useMutation({
        mutationFn: () => deleteBanner(),
        onSuccess: () => {
            setBannerData(null);
            queryClient.invalidateQueries("banner-settings")
            toast.success(t("banner").deleteSuccess);
        },
        onError: (error) => toast.error(error?.response?.data?.message || t("common").error),
    });

    const { mutate: updateAdvertisementMutation, isPending: isUpdating } = useMutation({
        mutationFn: ({ id, data }) => updateAdvertisement(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries("advertisements")
            toast.success(t("advertisements").updateSuccess);
            closeModal();
        },
        onError: (error) => toast.error(error?.response?.data?.message || t("common").error),
    });


    useEffect(() => {
        if (bannerListing?.data) {
            const filteredData = Object.fromEntries(
                Object.entries(bannerListing.data).filter(
                    ([_, value]) => value !== null && value !== undefined
                )
            );

            if (filteredData.bannerUrl || filteredData.bannerTitle || filteredData.bannerDescription) {
                setBannerData({
                    ...filteredData,
                    imageFile: null,
                    imagePreview: filteredData.bannerUrl || null,
                    title: filteredData.bannerTitle,
                    description: filteredData.bannerDescription
                });
            } else {
                setBannerData(null);
            }
        } else {
            setBannerData(null);
        }
    }, [bannerListing]);


    const closeModal = () => {
        setIsPopupOpen(false);
        setIsEditMode(false)
        setFormData({
            restaurantId: "",
            hotelId: "",
            placement: "",
            priority: 0,
            startDate: "",
            _id: "",
            endDate: "",
            image: null,
            imagePreview: null,
        });
        setErrors({});
    };

    const closeBannerModal = () => {
        setIsBannerEditOpen(false)
        setBannerData(prev => prev ? {
            ...prev,
            imageFile: null,
            imagePreview: null
        } : null);
        setBannerErrors({});
    };

    const openBannerEditModal = () => {
        if (!bannerData) {
            setBannerData({
                title: "",
                description: "",
                imageFile: null,
                imagePreview: null
            });
        }
        setIsBannerEditOpen(true);
    };


    const handleDeleteBanner = async () => {
        const result = await Swal.fire({
            title: t("subAdmin").deleteConfirm.title,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t("subAdmin").deleteConfirm.confirm,
            cancelButtonText: t("subAdmin").deleteConfirm.cancel,
        });

        if (result.isConfirmed) {
            deleteBannerMutation();
        }
    };

    const validateForm = (data) => {
        const newErrors = {};
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Entity validation
        if (selected === "restaurants" && !data.restaurantId)
            newErrors.restaurantId = t("advertisements").selectRestaurant;
        if (selected === "hotels" && !data.hotelId)
            newErrors.hotelId = t("advertisements").selectHotel;

        if (data.placement === "")
            newErrors.placement = t("advertisements").errors.placement;


        if (data.priority === undefined || data.priority === "" || isNaN(data.priority)) {
            newErrors.priority = t("advertisements").errors.priorityRequired;
        } else if (Number(data.priority) <= 0) {
            newErrors.priority = t("advertisements").errors.priorityGreaterThanZero;
        }


        if (!data.startDate) {
            newErrors.startDate = t("advertisements").errors.startDateRequired;
        }

        if (!data.endDate) {
            newErrors.endDate = t("advertisements").errors.endDateRequired;
        } else if (data.startDate && new Date(data.endDate) < new Date(data.startDate)) {
            newErrors.endDate = t("advertisements").errors.endDateAfterStartDate;
        }

        return newErrors;
    };


    const validateBannerForm = (data) => {
        const newErrors = {};
        if (!data?.imagePreview) newErrors.image = "Image is required"
        return newErrors;
    };

    const handleAddAdvertisement = () => {
        const newErrors = validateForm(formData);
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        const payload = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
            if (value !== null && value !== "") {
                // Skip _id field when adding new advertisement
                if (key === '_id' && !isEditMode) return;
                payload.append(key, value);
            }
        });

        if (isEditMode) {
            updateAdvertisementMutation({ id: formData._id, data: payload });
        } else {
            addAdvertisementMutation(payload);
        }
    };

    const handleUpdateBanner = () => {
        // if (!bannerData) {
        //     setBannerErrors({ title: "Title is required", description: "Description is required" });
        //     return;
        // }

        const newErrors = validateBannerForm(bannerData);
        setBannerErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        const payload = new FormData();
        payload.append('title', bannerData.title);
        payload.append('description', bannerData.description);
        if (bannerData.imageFile) {
            payload.append('image', bannerData.imageFile);
        }

        updateBannerMutation(payload);
    };

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === "file") {
            const imagePreview = URL.createObjectURL(files[0]);
            setFormData((prev) => ({ ...prev, image: files[0], imagePreview }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleBannerChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === "file") {
            const imagePreview = URL.createObjectURL(files[0]);
            setBannerData((prev) => ({
                ...prev,
                imageFile: files[0],
                imagePreview
            }));
        } else {
            setBannerData((prev) => ({ ...prev, [name]: value }));
        }
        setBannerErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: t("subAdmin").deleteConfirm.title,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t("subAdmin").deleteConfirm.confirm,
            cancelButtonText: t("subAdmin").deleteConfirm.cancel,
        });

        if (result.isConfirmed) {
            try {
                await deleteAdvertisement(id);
                queryClient.invalidateQueries(["advertisements"]);
                toast.success(t("common").deleteSuccess);
            } catch (error) {
                toast.error(error?.response?.data?.message || t("common").error);
            }
        }
    };

    return (
        <>
            {/* Banner Section - Conditionally Rendered */}
            {bannerListing?.data?.bannerUrl ? (
                <div className="relative rounded-[15px] overflow-hidden mb-8 shadow-lg group">
                    <img
                        src={bannerListing?.data?.bannerUrl}
                        alt="Advertisement Banner"
                        className="w-full h-[200px] object-cover transition-all duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                        <div className="text-center text-white">
                            <h1 className="text-3xl font-bold mb-2">{bannerListing?.data?.bannerTitle}</h1>
                            <p className="text-lg">{bannerListing?.data?.bannerDescription}</p>
                        </div>
                    </div>

                    {/* Edit and Delete Icons */}
                    <div className="absolute top-4 right-4 flex gap-2">
                        <button
                            onClick={openBannerEditModal}
                            className="bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-700 p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
                            title="Edit Banner"
                        >
                            <IoMdCreate size={20} />
                        </button>
                        <button
                            onClick={handleDeleteBanner}
                            disabled={isDeletingBanner}
                            className="bg-white bg-opacity-90 hover:bg-opacity-100 text-red-600 p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110 disabled:opacity-50"
                            title="Delete Banner"
                        >
                            <MdDelete size={20} />
                        </button>
                    </div>
                </div>
            ) : (

                <div className="rounded-[15px] border-2 border-dashed border-gray-300 mb-8 p-12 text-center bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
                    <RiImageAddFill size={48} className="text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">{t("banner").noBannerAdded}</h3>
                    <p className="text-gray-500 mb-6">{t("banner").bannerMessage}</p>
                    <button
                        onClick={openBannerEditModal}
                        className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2 mx-auto"
                    >
                        <RiImageAddFill size={20} />
                        {t("banner").addBanner}
                    </button>
                </div>
            )}

            {/* Controls Section */}
            <div className="flex justify-between items-center gap-3 mb-6 flex-wrap">
                <div className="flex gap-3 items-center flex-wrap">
                    <button
                        className={`px-6 py-3 rounded-full font-semibold transition-all duration-200 ${selected === "hotels"
                            ? "bg-green-600 text-white shadow-lg"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                        onClick={() => setSelected("hotels")}
                    >
                        {t("common").hotel}

                    </button>
                    <button
                        className={`px-6 py-3 rounded-full font-semibold transition-all duration-200 ${selected === "restaurants"
                            ? "bg-green-600 text-white shadow-lg"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                        onClick={() => setSelected("restaurants")}
                    >
                        {t("common").restaurant}

                    </button>
                </div>
                <button
                    onClick={() => {
                        setIsPopupOpen(true);
                        setIsEditMode(false);
                    }}
                    className="rounded-lg bg-green-600 hover:bg-green-700 text-white px-6 py-3 font-semibold transition-colors duration-200 shadow-lg flex items-center gap-2"
                >
                    <RiImageAddFill size={20} />
                    {t("advertisements").addNew}
                </button>
            </div>

            {/* Advertisement Table */}
            <div className="bg-white border border-gray-200 rounded-[15px] overflow-hidden shadow-sm">
                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : isError ? (
                    <div className="text-center py-8">
                        <p className="text-red-500 text-lg">{error.message}</p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="py-5 px-6 text-left font-semibold text-gray-700">{selected === "restaurants" ? t("common").restaurant : t("common").hotel}</th>
                                        <th className="py-5 px-6 text-left font-semibold text-gray-700">{t("advertisements").placement}</th>
                                        <th className="py-5 px-6 text-left font-semibold text-gray-700">{t("advertisements").priority}</th>
                                        <th className="py-5 px-6 text-left font-semibold text-gray-700">{t("advertisements").startDate}</th>
                                        <th className="py-5 px-6 text-left font-semibold text-gray-700">{t("advertisements").endDate}</th>
                                        <th className="py-5 px-6 text-left font-semibold text-gray-700">{t("common").actions}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {advertisements.map((ad, index) => (
                                        <tr
                                            key={ad._id}
                                            className={`border-b border-gray-100 hover:bg-gray-50 transition-colors duration-150 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                                }`}
                                        >
                                            <td className="py-4 px-6 font-medium">
                                                {selected === "restaurants" ? ad.restaurantId?.name : ad.hotelId?.name}
                                            </td>
                                            <td className="py-4 px-6">
                                                <div className="flex flex-wrap gap-2">
                                                    {ad.placement
                                                        .split(',')
                                                        .map((place, idx) => {
                                                            const option = placementOptions.find(opt => opt.value === place.trim());
                                                            return (
                                                                <span
                                                                    key={idx}
                                                                    className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium"
                                                                >
                                                                    {option ? option.label : place.trim()}
                                                                </span>
                                                            );
                                                        })}
                                                </div>
                                            </td>

                                            <td className="py-4 px-6">
                                                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                                                    {ad.priority}
                                                </span>
                                            </td>
                                            <td className="py-4 px-6 text-gray-600">
                                                {new Date(ad.startDate).toLocaleDateString()}
                                            </td>
                                            <td className="py-4 px-6 text-gray-600">
                                                {new Date(ad.endDate).toLocaleDateString()}
                                            </td>
                                            <td className="py-4 px-6">
                                                <div className="flex gap-3 items-center">
                                                    {/* <Link
                                                        to={`/advertisement/${ad._id}`}
                                                        className="text-blue-600 hover:text-blue-800 transition-colors duration-200 p-2 rounded-lg hover:bg-blue-50"
                                                        title="View"
                                                    >
                                                        <IoMdEye size={20} />
                                                    </Link> */}

                                                    <Link
                                                        to="#"
                                                        onClick={() => {
                                                            setIsPopupOpen(true)
                                                            setIsEditMode(true)
                                                            setFormData({
                                                                restaurantId: ad?.restaurantId?._id ? ad?.restaurantId?._id : "",
                                                                hotelId: ad?.hotelId?._id ? ad?.hotelId?._id : "",
                                                                _id: ad._id,
                                                                placement: ad.placement,
                                                                priority: ad.priority,
                                                                startDate: ad.startDate ? new Date(ad.startDate).toISOString().split("T")[0] : "",
                                                                endDate: ad.endDate ? new Date(ad.endDate).toISOString().split("T")[0] : "",
                                                            });
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 transition-colors duration-200 p-2 rounded-lg hover:bg-blue-50"
                                                        title="View / Edit"
                                                    >
                                                        <IoMdEye size={20} />
                                                    </Link>

                                                    <button
                                                        onClick={() => handleDelete(ad._id)}
                                                        className="text-red-600 hover:text-red-800 transition-colors duration-200 p-2 rounded-lg hover:bg-red-50"
                                                        title="Delete"
                                                    >
                                                        <MdDelete size={20} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {advertisements.length === 0 && (
                            <div className="text-center py-12">
                                <p className="text-gray-500 text-lg">{t("advertisements").noAdvertisements}</p>
                            </div>
                        )}
                    </>
                )}
            </div>

            {advertisements.length > 0 && (
                <div className="mt-6">
                    <Pagination page={pagination.page} totalPages={pagination.totalPages} setPage={setPage} />
                </div>
            )}


            <PopupModal isOpen={isPopupOpen} onClose={closeModal}>
                <div className="p-8">
                    <h2 className="text-2xl font-bold mb-6 text-gray-800">{isEditMode ? t("advertisements").updateTitle : t("advertisements").addNew}</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {/* Entity Selection */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {selected === "restaurants" ? t("common").restaurant : t("common").hotel} *
                            </label>
                            {selected === "restaurants" ? (
                                <select
                                    name="restaurantId"
                                    onChange={handleChange}
                                    value={formData.restaurantId}
                                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors.restaurantId ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                >
                                    <option value="">{t("advertisements").selectRestaurant}</option>
                                    {listingData?.data?.map((r) => (
                                        <option key={r._id} value={r._id}>{r.name}</option>
                                    ))}
                                </select>
                            ) : (
                                <select
                                    name="hotelId"
                                    onChange={handleChange}
                                    value={formData.hotelId}
                                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors.hotelId ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                >
                                    <option value="">{t("advertisements").selectHotel}</option>
                                    {listingData?.data?.map((h) => (
                                        <option key={h._id} value={h._id}>{h.name}</option>
                                    ))}
                                </select>
                            )}
                            {errors.restaurantId && <p className="text-red-500 text-sm mt-1">{errors.restaurantId}</p>}
                            {errors.hotelId && <p className="text-red-500 text-sm mt-1">{errors.hotelId}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t("advertisements").placement} *
                            </label>
                            <Select
                                isMulti
                                name="placement"
                                options={placementOptions}
                                value={placementOptions.filter((opt) =>
                                    (formData?.placement || []).includes(opt.value)
                                )}
                                onChange={(selected) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        placement: selected.map((s) => s.value),
                                    }));
                                }}
                                placeholder={t("advertisements").placementPlaceholder}
                                classNamePrefix="react-select"
                            />
                            {errors.placement && (
                                <p className="text-red-500 text-sm mt-1">{errors.placement}</p>
                            )}
                        </div>


                        {/* Priority */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t("advertisements").priority} *
                            </label>
                            <input
                                type="number"
                                name="priority"
                                placeholder={t("advertisements").priority}
                                value={formData.priority}
                                onChange={handleChange}
                                min="0"
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                            {errors.priority && <p className="text-red-500 text-sm mt-1">{errors.priority}</p>}
                        </div>

                        {/* Start Date */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t("advertisements").startDate} *
                            </label>
                            <input
                                type="date"
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleChange}
                                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors.startDate ? 'border-red-500' : 'border-gray-300'
                                    }`}
                            />
                            {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>}
                        </div>

                        {/* End Date */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t("advertisements").endDate} *
                            </label>
                            <input
                                type="date"
                                name="endDate"
                                value={formData.endDate}
                                onChange={handleChange}
                                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors.endDate ? 'border-red-500' : 'border-gray-300'
                                    }`}
                            />
                            {errors.endDate && <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>}
                        </div>


                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                        <button
                            onClick={closeModal}
                            className="px-6 py-3 bg-gray-400 hover:bg-gray-500 text-white font-medium rounded-lg transition-colors duration-200"
                        >
                            {t("common").cancel}
                        </button>
                        <button
                            onClick={handleAddAdvertisement}
                            disabled={isAdding || isUpdating}
                            className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isAdding ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    {t("common").loading}
                                </>
                            ) : (
                                t("common").add
                            )}
                        </button>
                    </div>
                </div>
            </PopupModal>


            <PopupModal isOpen={isBannerEditOpen} onClose={closeBannerModal}>
                <div className="p-8">
                    <h2 className="text-2xl font-bold mb-6 text-gray-800">
                        {bannerData ? t("banner").update : t("banner").addBanner}
                    </h2>

                    <div className="grid grid-cols-1 gap-6 mb-6">
                        {/* Banner Image Upload */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t("banner").bannerImage}
                            </label>
                            <div className={`relative border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center h-[200px] w-full cursor-pointer transition-all duration-200 ${bannerErrors.image ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-green-500 hover:bg-green-50'
                                }`}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleBannerChange}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                    name="image"
                                />


                                {bannerData?.imagePreview ? (
                                    <div className="relative w-full h-full">
                                        <img
                                            src={bannerData.imagePreview}
                                            alt="Banner Preview"
                                            className="w-full h-full object-contain rounded-lg"
                                        />
                                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center">
                                            <RiImageAddFill size={32} className="text-white opacity-0 hover:opacity-100 transition-opacity duration-200" />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center">
                                        <RiImageAddFill size={48} className="text-gray-400 mx-auto mb-3" />
                                        <p className="text-gray-600 font-medium">
                                            {bannerData ? t("banner").clickChange : t("banner").clickUpload}
                                        </p>
                                        <p className="text-gray-400 text-sm mt-1">
                                            {bannerData ? t("banner").currentReplace : t("banner").recommendedSize}
                                        </p>
                                    </div>
                                )}
                            </div>
                            {bannerErrors.image && <p className="text-red-500 text-sm mt-2">{bannerErrors.image}</p>}
                        </div>

                        {/* Title Input */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t("banner").title} *
                            </label>
                            <input
                                type="text"
                                name="title"
                                placeholder={t("banner").titlePlaceholder}
                                value={bannerData?.title || ""}
                                onChange={handleBannerChange}
                                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${bannerErrors.title ? 'border-red-500' : 'border-gray-300'
                                    }`}
                            />
                            {bannerErrors.title && <p className="text-red-500 text-sm mt-1">{bannerErrors.title}</p>}
                        </div>

                        {/* Description Input */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t("banner").description} *
                            </label>
                            <textarea
                                name="description"
                                placeholder={t("banner").descriptionPlaceholder}
                                value={bannerData?.description || ""}
                                onChange={handleBannerChange}
                                rows="3"
                                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${bannerErrors.description ? 'border-red-500' : 'border-gray-300'
                                    }`}
                            />
                            {bannerErrors.description && <p className="text-red-500 text-sm mt-1">{bannerErrors.description}</p>}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-4 pt-6 ">
                        <button
                            onClick={closeBannerModal}
                            className="px-6 py-3 bg-gray-400 hover:bg-gray-500 text-white font-medium rounded-lg transition-colors duration-200"
                        >
                            {t("common").cancel}
                        </button>
                        <button
                            onClick={handleUpdateBanner}
                            disabled={isUpdatingBanner}
                            className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isUpdatingBanner ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    {t("common").loading}
                                </>
                            ) : (
                                bannerData ? t("banner").update : t("banner").addBanner
                            )}
                        </button>
                    </div>
                </div>
            </PopupModal>
        </>
    );
};

export default AdvertisementList;
